import {
	ReactElement,
	useState,
	useEffect,
	useCallback
} from "react";
import {
	Dialog,
	ReorderableList,
	Toolbar,
	Typography
} from "client/Components";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import {
	SharedTileGroupDefaultOrderModel,
	getSharedTileGroupDefaultOrderList,
	saveSharedTileGroupDefaultOrderList
} from "client/Api/Workspace/workspace";
import {
	AlertManager,
	useAlerts,
	useOpenDialog,
	useUnmountedCancellation
} from "client/Hooks";

export type SharedTileGroupDefaultOrderListProps = {
	onClose: () => void;
};

const {
	Header,
	Body,
	Footer
} = Dialog;

export const SharedTileGroupDefaultOrderList = (props: SharedTileGroupDefaultOrderListProps): ReactElement => {
	const { onClose } = props;
	const [items, setItems] = useState<readonly SharedTileGroupDefaultOrderModel[]>([]);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useEffect(
		() => {
			async function fetchItems(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const models: SharedTileGroupDefaultOrderModel[] = await getSharedTileGroupDefaultOrderList(signal);

					setItems(sortBy(models, (model) => model.order));
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			setItems(
				(items) => {
					const copy: SharedTileGroupDefaultOrderModel[] = [...items];
					const item: SharedTileGroupDefaultOrderModel = copy.splice(fromIndex, 1)[0];
					copy.splice(toIndex, 0, item);
					return copy;
				}
			);
		},
		[]
	);

	const saveHandler = async (): Promise<void> => {
		const newOrder: number[] = items.map((model) => model.id);

		setIsBusy(true);

		try {
			await saveSharedTileGroupDefaultOrderList(
				newOrder,
				globalCancellation
			);
			onClose();
			setIsBusy(false);
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}
			console.error(e);
			alertManager.showError(localize("_ChangesNotSaved"));
			setIsBusy(false);
		}
	};

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={onClose}
			trackingId="manage-default-order-for-shared-tile-groups-sidebar"
		>
			<Header
				title={localize("_ManageDefaultOrderForSharedGroups")}
				onClose={onClose}
				hasBorder
			/>
			<Body>
				<ReorderableList
					onItemMove={itemMoveHandler}
					hasSeparator
					getOverlayText={activeIndex => items[activeIndex].name}
				>
					{items.map(item => (
						<ReorderableList.RowItem
							key={item.id}
						>
							<Typography weight="bold">
								{item.name}
							</Typography>
						</ReorderableList.RowItem>
					))}
				</ReorderableList>
			</Body>
			<Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={saveHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Footer>
		</Dialog>
	);
};

SharedTileGroupDefaultOrderList.displayName = "SharedTileGroupDefaultOrderList";

export function useSharedTileGroupDefaultOrderList(): [
	element: ReactElement | null,
	open: () => Promise<void>
] {
	const [element, openInternal] = useOpenDialog<{}>(
		(_, onClose) => <SharedTileGroupDefaultOrderList onClose={onClose} />
	);

	const openHandler = useCallback(
		(): Promise<void> => {
			return openInternal({});
		},
		[openInternal]
	);

	return [
		element,
		openHandler
	];
}